import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { blogPosts } from "@/data/content";
import useSeo from "@/hooks/useSeo";

export default function BlogDetail() {
    const { slug } = useParams();
    const post = blogPosts.find((p) => p.slug === slug);
    useSeo({
        title: post ? post.title : "News",
        description: post?.excerpt,
        path: post ? `/blog/${post.slug}` : "/blog",
    });
    if (!post) return <Navigate to="/blog" replace />;

    return (
        <div data-testid="blog-detail-page" className="bg-white">
            <article>
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" /> All news
                    </Link>
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600 mb-4">
                        {post.category} · {post.readTime}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tighter leading-[1.02]">
                        {post.title}
                    </h1>
                    <div className="mt-5 text-sm text-slate-500 font-semibold">
                        {post.date}
                    </div>
                </div>

                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
                    <div className="rounded-3xl overflow-hidden aspect-[16/9]">
                        <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                </div>

                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="prose prose-slate max-w-none text-lg leading-relaxed text-slate-700 space-y-5" data-testid="blog-body">
                        <p className="text-xl text-slate-800 font-medium">{post.excerpt}</p>

                        {post.body ? (
                            post.body.map((block, idx) => {
                                if (block.type === "h2") {
                                    return (
                                        <h2
                                            key={idx}
                                            className="font-display text-3xl font-black text-slate-900 tracking-tighter mt-10"
                                        >
                                            {block.text}
                                        </h2>
                                    );
                                }
                                if (block.type === "ul") {
                                    return (
                                        <ul key={idx} className="list-disc pl-6 space-y-2">
                                            {block.items.map((it) => (
                                                <li key={it}>{it}</li>
                                            ))}
                                        </ul>
                                    );
                                }
                                if (block.type === "ol") {
                                    return (
                                        <ol key={idx} className="list-decimal pl-6 space-y-2">
                                            {block.items.map((it) => (
                                                <li key={it}>{it}</li>
                                            ))}
                                        </ol>
                                    );
                                }
                                return <p key={idx}>{block.text}</p>;
                            })
                        ) : (
                            <>
                                <p>
                                    Logistics isn&apos;t a single decision — it&apos;s thousands of small ones,
                                    made every hour, across a network that never sleeps. The teams
                                    that win are the ones that can measure those decisions, learn
                                    from them and keep shaving friction from their operation.
                                </p>
                                <h2 className="font-display text-3xl font-black text-slate-900 tracking-tighter mt-10">
                                    What we set out to solve
                                </h2>
                                <p>
                                    Over the last year we instrumented our fleet and warehouse
                                    floors with a few new data streams — telemetry, dwell times, dock
                                    throughput — and asked a simple question: where does an
                                    average day actually leak time and money? The answers surprised
                                    us in places, confirmed us in others, and gave us a short list
                                    of interventions that were worth trying.
                                </p>
                                <h2 className="font-display text-3xl font-black text-slate-900 tracking-tighter mt-10">
                                    Three things that moved the needle
                                </h2>
                                <ol className="list-decimal pl-6 space-y-3">
                                    <li>
                                        Tighter appointment-window discipline at inbound docks (reduced
                                        dwell by 22%).
                                    </li>
                                    <li>
                                        A better routing engine for middle-mile consolidation (fuel
                                        down 11%).
                                    </li>
                                    <li>
                                        ePOD standardisation across all lanes (average DSO reduced by
                                        4 days).
                                    </li>
                                </ol>
                                <p>
                                    None of these are flashy. That&apos;s the point. The operators we
                                    admire most don&apos;t chase novelty — they chase repeatable,
                                    measurable wins. We&apos;ll take that trade every time.
                                </p>
                            </>
                        )}
                    </div>

                    <div className="mt-16 rounded-3xl bg-slate-900 text-white p-10 text-center">
                        <h3 className="font-display text-3xl font-black tracking-tighter">
                            Want to run numbers like this on your operation?
                        </h3>
                        <p className="mt-3 text-slate-300 max-w-lg mx-auto">
                            Start with a free lane analysis — we&apos;ll give you an honest read
                            and a quote you can actually compare.
                        </p>
                        <Link
                            to="/quote"
                            className="mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-slate-900 font-bold hover:bg-blue-600 hover:text-white transition-colors"
                        >
                            Get an instant quote <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </article>
        </div>
    );
}
